import type { ReactNode } from 'react'
import styles from './ControlRail.module.css'

interface RailButtonProps {
  /**
   * The accessible name. For a toggle this must NOT change when the state
   * changes: a control whose name flips between "Mute" and "Unmute" cannot be
   * queried for its current state, only inferred from it, and it gets
   * announced twice on every press.
   */
  label: string
  /** Present only for toggles. State travels through this, not through label. */
  pressed?: boolean
  onClick(): void
  children: ReactNode
  /** Short visible caption under the glyph. */
  caption?: string
  variant?: 'default' | 'primary' | 'danger'
  disabled?: boolean
  testId?: string
}

export function RailButton({
  label,
  pressed,
  onClick,
  children,
  caption,
  variant = 'default',
  disabled = false,
  testId,
}: RailButtonProps) {
  const className = [
    styles.button,
    variant === 'primary' ? styles.primary : '',
    variant === 'danger' ? styles.danger : '',
  ]
    .filter(Boolean)
    .join(' ')

  return (
    <button
      type="button"
      className={className}
      onClick={onClick}
      aria-label={label}
      {...(pressed === undefined ? {} : { 'aria-pressed': pressed })}
      disabled={disabled}
      data-testid={testId}
    >
      {children}
      {caption ? <span className={styles.label}>{caption}</span> : null}
    </button>
  )
}

/**
 * The bottom rail.
 *
 * Every primary control is visible and labelled. Nothing here is behind a
 * gesture: Google shipped mute behind a double-tap on the waveform, then
 * reverted it as a hidden gesture that lacked clarity, and there is no reason
 * to relearn that.
 */
export function ControlRail({ children }: { children: ReactNode }) {
  return (
    <div className={styles.rail} role="group">
      {children}
    </div>
  )
}
